/**
 * O botão do cabeçalho e Ctrl/⌘ K usam a mesma paleta. A rota /busca contém
 * recentes e atalhos de escopo que também abrem esse painel.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import type { ItemDaBuscaGlobal, TipoDeResultadoDaBusca } from '@dv/contracts'
import { buscarGlobalmente } from '../api/recursos.ts'
import { ErroDaApi } from '../api/cliente.ts'
import { Icone } from '../shell/icone.tsx'
import { haSobreposicaoAberta, Sobreposicao } from './sobreposicao.tsx'
import { useTextos } from '../textos.ts'
import { GRUPOS_DA_BUSCA, SINGULARES_DA_BUSCA, TEXTOS_DA_BUSCA, TIPOS_DA_BUSCA } from './busca-textos.ts'
import { useEntidadeAtiva } from '../shell/contexto-de-entidade.tsx'
import { deTipoDaBusca, destinoDaEntidade } from './link-da-entidade-regras.ts'

/*
  O rótulo do grupo é o nome do produto, e não o nome do tipo: o contrato diz
  `colaborador` e a tela diz "Equipe". E o pedido aparece com o nome inteiro, "Pedidos
  de Venda", que é como o produto nomeia o registro em toda parte.

  O DESTINO de cada tipo não mora aqui: sai do mapa único do link de entidade
  (`link-da-entidade-regras.ts`, D-136, DS-533), o mesmo da inbox e dos relatórios. A
  paleta é instrumento de navegação do shell e abre o resultado na MESMA aba: a pessoa
  pediu para IR até lá (`specs/04-TELAS/2.2-busca-global.md`, "Enter abre o deep-link do
  ativo"). O resultado é um `<a href>` de verdade, e por isso Ctrl+clique e o botão do meio
  abrem aba nova quando a pessoa quer.
*/
const ICONE: Readonly<Record<TipoDeResultadoDaBusca, string>> = {
  empresa: 'building-2',
  contato: 'users',
  negocio: 'briefcase',
  pedido: 'receipt',
  fornecedor: 'truck',
  campanha: 'radio-tower',
  colaborador: 'user-round',
  tarefa: 'square-check-big',
}
interface ContextoDaBusca {
  readonly abrir: (texto?: string, escopo?: TipoDeResultadoDaBusca | '') => void
  readonly recentes: readonly string[]
  readonly limparRecentes: () => void
}

const Contexto = createContext<ContextoDaBusca | null>(null)

export function useBuscaGlobal(): ContextoDaBusca {
  const valor = useContext(Contexto)
  if (valor === null) throw new Error('useBuscaGlobal precisa do ProvedorDaBuscaGlobal')
  return valor
}

function lerRecentes(chave: string): string[] {
  try {
    const bruto: unknown = JSON.parse(localStorage.getItem(chave) ?? '[]')
    return Array.isArray(bruto) ? bruto.filter((v): v is string => typeof v === 'string').slice(0, 6) : []
  } catch {
    return []
  }
}

export function ProvedorDaBuscaGlobal({ usuarioId, children }: { usuarioId: string; children: ReactNode }) {
  const chave = `dv:buscas:${usuarioId}`
  const [aberta, setAberta] = useState(false)
  const [texto, setTexto] = useState('')
  const [escopo, setEscopo] = useState<TipoDeResultadoDaBusca | ''>('')
  const [recentes, setRecentes] = useState<string[]>(() => lerRecentes(chave))

  const abrir = useCallback((inicial = '', novoEscopo: TipoDeResultadoDaBusca | '' = '') => {
    setTexto(inicial)
    setEscopo(novoEscopo)
    setAberta(true)
  }, [])
  const limparRecentes = useCallback(() => {
    setRecentes([])
    localStorage.removeItem(chave)
  }, [chave])

  const registrar = useCallback((consulta: string) => {
    const proximas = [consulta, ...lerRecentes(chave).filter(r => r.toLocaleLowerCase('pt-BR') !== consulta.toLocaleLowerCase('pt-BR'))].slice(0, 6)
    localStorage.setItem(chave, JSON.stringify(proximas))
    setRecentes(proximas)
  }, [chave])

  // O perfil chega depois do shell. Trocar a chave recarrega o histórico da pessoa,
  // mas não remonta o provedor — uma busca já aberta não pode fechar só porque o
  // nome do usuário terminou de carregar no rodapé.
  useEffect(() => { setRecentes(lerRecentes(chave)) }, [chave])

  useEffect(() => {
    const atalho = (evento: KeyboardEvent): void => {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 'k') {
        evento.preventDefault()
        // Com um diálogo aberto o atalho é CONSUMIDO e não abre nada (PK-02): a Busca nascia por
        // baixo da modal, virava o topo do isolamento e deixava a modal visível e inerte.
        if (haSobreposicaoAberta()) return
        abrir()
      }
    }
    // Captura antes dos controles da página: Ctrl K também é atalho nativo de alguns
    // navegadores, e só nesta fase a aplicação consegue assumir a combinação.
    window.addEventListener('keydown', atalho, { capture: true })
    return () => { window.removeEventListener('keydown', atalho, { capture: true }) }
  }, [abrir])

  const valor = useMemo(() => ({ abrir, recentes, limparRecentes }), [abrir, recentes, limparRecentes])
  return (
    <Contexto.Provider value={valor}>
      {children}
      {aberta ? (
        <PainelDaBuscaDoContexto
          textoInicial={texto}
          escopoInicial={escopo}
          aoFechar={() => { setAberta(false) }}
          aoRegistrar={registrar}
        />
      ) : null}
    </Contexto.Provider>
  )
}

/** A busca mistura mestres e fatos; trocar o header invalida o painel inteiro. */
function PainelDaBuscaDoContexto(props: Parameters<typeof PainelDaBusca>[0]) {
  const entidadeAtiva = useEntidadeAtiva()
  return <PainelDaBusca key={entidadeAtiva ?? 'sem-entidade'} {...props} />
}

function PainelDaBusca({
  textoInicial,
  escopoInicial,
  aoFechar,
  aoRegistrar,
}: {
  textoInicial: string
  escopoInicial: TipoDeResultadoDaBusca | ''
  aoFechar: () => void
  aoRegistrar: (consulta: string) => void
}) {
  const t = useTextos(TEXTOS_DA_BUSCA)
  const rotuloDoGrupo = useTextos(GRUPOS_DA_BUSCA)
  const rotuloDoItem = useTextos(SINGULARES_DA_BUSCA)
  const [texto, setTexto] = useState(textoInicial)
  const [escopo, setEscopo] = useState<TipoDeResultadoDaBusca | ''>(escopoInicial)
  const [grupos, setGrupos] = useState<Awaited<ReturnType<typeof buscarGlobalmente>>['grupos']>([])
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<{ origem: 'api'; mensagem: string } | { origem: 'local' } | null>(null)
  const [ativo, setAtivo] = useState(0)
  const [tentativa, setTentativa] = useState(0)
  const corpo = useRef<HTMLDivElement>(null)
  const rolarParaAtivo = useRef(false)
  const entrada = useRef<HTMLInputElement>(null)
  const navegar = useNavigate()
  const itens = useMemo(() => grupos.flatMap((g) => g.itens), [grupos])

  useLayoutEffect(() => {
    if (!rolarParaAtivo.current) return
    corpo.current?.querySelectorAll('[data-teste="resultado-busca"]')[ativo]?.scrollIntoView({ block: 'nearest' })
    rolarParaAtivo.current = false
  }, [ativo, itens])

  useEffect(() => { entrada.current?.focus() }, [])
  useEffect(() => {
    const q = texto.trim()
    setGrupos([])
    if (q.length < 2) {
      setGrupos([])
      setCarregando(false)
      setErro(null)
      return
    }
    const controle = new AbortController()
    setCarregando(true)
    setErro(null)
    const espera = window.setTimeout(() => {
      void buscarGlobalmente(q, escopo, controle.signal)
        .then((resposta) => {
          if (controle.signal.aborted) return
          aoRegistrar(q)
          setGrupos(resposta.grupos)
          setAtivo(0)
        })
        .catch((causa: unknown) => {
          if (!controle.signal.aborted) {
            setErro(causa instanceof ErroDaApi ? { origem: 'api', mensagem: causa.message } : { origem: 'local' })
          }
        })
        .finally(() => {
          if (!controle.signal.aborted) setCarregando(false)
        })
    }, 320)
    return () => {
      window.clearTimeout(espera)
      controle.abort()
    }
  }, [texto, escopo, tentativa, aoRegistrar])

  const escolher = (item: ItemDaBuscaGlobal): void => {
    const destino = destinoDaEntidade(deTipoDaBusca(item.tipo), item.id)
    aoFechar()
    if (destino !== null) void navegar(destino)
  }

  // O diálogo é a própria sobreposição (lote U1): o `role="dialog"` sobe para o
  // container da primitive, que é quem traz trava de foco, `inert` no fundo e
  // devolução do foco. O painel branco continua sendo só o painel branco.
  return (
    <Sobreposicao
      aberto
      aoFechar={aoFechar}
      rotulo={t('titulo')}
      aoAbrirFocar={entrada}
      aoPressionarFora={aoFechar}
      // A única sobreposição com desfoque: tinta a 50% e 4 px (spec 2.2:21; DS-517).
      className="fixed inset-0 z-50 bg-ink/50 px-3 pt-[8vh] backdrop-blur-xs"
    >
      <section
        className="mx-auto flex max-h-[78vh] flex-col w-full max-w-4xl overflow-hidden rounded-2xl border border-white/30 bg-white shadow-2xl"
        onMouseDown={(evento) => { evento.stopPropagation() }}
        data-teste="painel-busca-global"
      >
        <div className="flex items-center gap-3 border-b border-hairline px-4">
          <Icone nome="search" className="h-5 w-5 text-navy" />
          <input
            ref={entrada}
            type="search"
            value={texto}
            onChange={(evento) => { setTexto(evento.target.value) }}
            onKeyDown={(evento) => {
              if (evento.key === 'Escape') aoFechar()
              if (evento.key === 'ArrowDown') {
                evento.preventDefault()
                rolarParaAtivo.current = true
                setAtivo((n) => itens.length === 0 ? 0 : (n + 1) % itens.length)
              }
              if (evento.key === 'ArrowUp') {
                evento.preventDefault()
                rolarParaAtivo.current = true
                setAtivo((n) => itens.length === 0 ? 0 : (n - 1 + itens.length) % itens.length)
              }
              if (evento.key === 'Enter' && itens[ativo] !== undefined) escolher(itens[ativo])
            }}
            placeholder={t('exemplo')}
            aria-label={t('entrada')}
            className="h-14 min-w-0 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-faint"
            data-teste="entrada-busca-global"
          />
          <kbd className="hidden rounded border border-hairline bg-paper px-2 py-1 text-[11px] text-muted sm:block">ESC</kbd>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-hairline bg-paper/60 px-4 py-2">
          {(['', ...TIPOS_DA_BUSCA] as const).map(valor => (
            <button
              key={valor}
              type="button"
              onClick={() => { setEscopo(valor) }}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors ${escopo === valor ? 'bg-navy text-white' : 'text-text2 hover:bg-navy/5'}`}
            >
              {valor === '' ? t('tudo') : rotuloDoGrupo(valor)}
            </button>
          ))}
        </div>

        <div ref={corpo} className="min-h-0 max-h-[58vh] overflow-y-auto p-2" aria-live="polite">
          {texto.trim().length < 2 ? (
            <div className="px-4 py-10 text-center">
              {/* Sem a lista dos tipos (D-203): as pílulas de escopo logo acima nomeiam os mesmos oito. */}
              <p className="text-sm font-medium text-navy">{t('minimo')}</p>
            </div>
          ) : carregando ? (
            <div className="space-y-2 p-2">
              <p className="text-sm text-muted">{t('buscando')}</p>
              {Array.from({ length: 4 }, (_, i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-paper2" />)}
            </div>
          ) : erro !== null ? (
            <div className="px-4 py-10 text-center text-sm text-red-700">
              <p>{erro.origem === 'api' ? erro.mensagem : t('falha')}</p>
              <button type="button" className="mt-3 rounded-lg border border-hairline px-3 py-2 text-navy hover:bg-paper"
                onClick={() => { setTentativa(n => n + 1); entrada.current?.focus() }}
              >{t('repetir')}</button>
            </div>
          ) : itens.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="text-sm font-medium text-navy">{t('vazio')}</p>
              <p className="mt-1 text-[12px] text-muted">{t('outroTermo')}</p>
            </div>
          ) : (
            grupos.map((grupo) => (
              <div key={grupo.tipo} className="mb-2">
                <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[.12em] text-faint">{rotuloDoGrupo(grupo.tipo)}</p>
                {grupo.itens.map((item) => {
                  const indice = itens.findIndex((candidato) => candidato.tipo === item.tipo && candidato.id === item.id)
                  const destino = destinoDaEntidade(deTipoDaBusca(item.tipo), item.id)
                  if (destino === null) return null
                  return (
                    <Link
                      key={`${item.tipo}:${item.id}`}
                      to={destino.to}
                      search={destino.search}
                      onMouseMove={() => { setAtivo(indice) }}
                      onClick={(evento) => {
                        // O clique simples navega aqui e fecha a paleta; com Ctrl, ⌘ ou Shift o
                        // navegador abre outra aba ou janela, e a paleta continua onde estava.
                        if (evento.button === 0 && !evento.ctrlKey && !evento.metaKey && !evento.shiftKey) aoFechar()
                      }}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${ativo === indice ? 'bg-navy/5' : 'hover:bg-paper'}`}
                      data-teste="resultado-busca"
                    >
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-paper2 text-navy">
                        <Icone nome={ICONE[item.tipo]} className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-ink">{item.titulo}</span>
                        <span className="block truncate text-[12px] text-muted">{rotuloDoItem(item.tipo)}{item.subtitulo === '' ? '' : ` · ${item.subtitulo}`}</span>
                      </span>
                      <Icone nome="arrow-right" className="h-4 w-4 text-faint" />
                    </Link>
                  )
                })}
              </div>
            ))
          )}
        </div>
        {/*
          O atalho de teclado só no desktop; a ressalva de acesso também no celular (D-203): ela
          muda a leitura do resultado, e sumia junto com o rodapé inteiro.
        */}
        <footer className="flex items-center justify-end border-t border-hairline bg-paper/70 px-4 py-2 text-[10px] text-faint sm:justify-between">
          <span className="hidden sm:inline">{t('teclado')}</span>
          <span>{t('acesso')}</span>
        </footer>
      </section>
    </Sobreposicao>
  )
}
